// /reset-password/$token — recibe el token del link enviado por email.
// PB endpoint: pb.collection('users').confirmPasswordReset(token, password,
// passwordConfirm). Si OK, redirige a /login.

import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ClientResponseError } from 'pocketbase';
import { ArrowLeft, CheckCircle2, Sun, Moon } from 'lucide-react';
import { pb, isLoggedIn } from '~/lib/pb';
import { applyTheme, getStoredTheme } from '~/lib/theme';
import type { Theme } from '~/lib/theme';
import { PasswordInput } from '~/components/PasswordInput/PasswordInput';
import styles from './login.module.css';

export const Route = createFileRoute('/reset-password/$token')({
  beforeLoad: () => {
    if (isLoggedIn()) {
      throw redirect({ to: '/' });
    }
  },
  component: ResetPasswordPage,
});

const MIN_PASSWORD_LENGTH = 8;

function ResetPasswordPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<Theme>(getStoredTheme());

  function handleToggleTheme() {
    const next: Theme = theme === 'light' ? 'dark' : 'light';
    applyTheme(next);
    setTheme(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await pb.collection('users').confirmPasswordReset(token, password, confirm);
      setSuccess(true);
      // Redirige a login después de 2.5s para que el usuario vea el mensaje.
      setTimeout(() => navigate({ to: '/login' }), 2500);
    } catch (err) {
      const msg =
        err instanceof ClientResponseError
          ? err.status === 400
            ? 'El link expiró o ya fue usado. Solicita uno nuevo desde "¿Olvidaste tu contraseña?".'
            : err.response?.message || err.message
          : 'No pudimos cambiar tu contraseña. Intenta de nuevo.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.login}>
      <button
        type="button"
        className={styles.login__themeToggle}
        onClick={handleToggleTheme}
        aria-label={`Cambiar a tema ${theme === 'light' ? 'oscuro' : 'claro'}`}
      >
        {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      <div className={styles.login__panel}>
        <header className={styles.login__header}>
          <div className={styles.login__brand}>
            <img
              className={styles.login__brandMark}
              src="/brand/isotipo.png"
              alt="Life Focus"
              width={48}
              height={48}
            />
            <h1 className={styles.login__brandLabel}>Nueva contraseña</h1>
          </div>
          <p className={`muted ${styles.login__subtitle}`}>
            Ingresa tu nueva contraseña. Mínimo {MIN_PASSWORD_LENGTH} caracteres.
          </p>
        </header>

        {!success ? (
          <form className={styles.login__form} onSubmit={handleSubmit}>
            <label className={styles.field}>
              <span className={styles.field__label}>Nueva contraseña</span>
              <PasswordInput
                className={styles.field__input}
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoFocus
              />
            </label>

            <label className={styles.field}>
              <span className={styles.field__label}>Confirmar contraseña</span>
              <PasswordInput
                className={styles.field__input}
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={loading}
              />
            </label>

            {error && (
              <p className={styles.login__error} role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              className={`${styles.button} ${styles['button--primary']}`}
              disabled={loading}
            >
              {loading ? 'Guardando…' : 'Cambiar contraseña'}
            </button>

            <div className={styles.login__forgotRow}>
              <Link to="/login" className={styles.login__forgotLink}>
                <ArrowLeft size={14} aria-hidden />
                <span>Volver al login</span>
              </Link>
            </div>
          </form>
        ) : (
          <div className={styles.successBox}>
            <div className={styles.successIcon}>
              <CheckCircle2 size={28} aria-hidden />
            </div>
            <h2 className={styles.successTitle}>Contraseña actualizada</h2>
            <p className={styles.successText}>
              Te llevamos al login para que entres con tu nueva contraseña…
            </p>
          </div>
        )}
      </div>

      <footer className={styles.login__footer}>
        <p className="subtle">© Life Focus. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
